import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layers } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import AssetPlayer from "@/components/assets/AssetPlayer";

// Student side: resources the teacher attached to this mission (learner-safe snapshots; marking happens on the server).
export function MissionAssets({ assignmentId, assets }) {
  const { t } = useI18n();
  if (!assets?.length) return null;
  return (
    <section className="mt-6 space-y-3" data-testid="mission-assets">
      <h2 className="font-cabinet font-bold text-lg flex items-center gap-2"><Layers className="w-5 h-5" /> {t("al.p.missionResources")}</h2>
      {assets.map((a) => (
        <div key={a.asset_id} className="nb-card bg-white p-4" data-testid={`mission-asset-${a.asset_id}`}>
          <div className="font-bold" dir="auto">{a.title}</div>
          {a.student_instructions && <p className="text-sm text-gray-600 mb-2" dir="auto">{a.student_instructions}</p>}
          <AssetPlayer family={a.family} language={a.language} content={a.content}
            onCheck={a.content.items || a.content.crossword ? async (answers) => (await api.post(`/assignments/${assignmentId}/assets/${a.asset_id}/check`, { answers })).data : null} />
        </div>
      ))}
    </section>
  );
}

// Teacher side (Mission Library render panel): attach compatible resources and preview the combined experience.
export function AssetAttach({ language, value, onChange }) {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/resources/assets", { params: { lang: language } }), api.get("/resources/assets", { params: { lang: language, mine: true } })])
      .then(([a, b]) => setRows([...a.data.items, ...b.data.items.filter((x) => x.status !== "draft" && !a.data.items.some((y) => y.asset_id === x.asset_id))])).catch(() => setRows([]));
    const pre = params.get("asset");
    if (pre && params.get("lang") === language && !value.some((v) => v.asset_id === pre)) onChange([...value, { asset_id: pre, language }]);
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id, on) => { setPreview(null); onChange(on ? [...value, { asset_id: id, language }] : value.filter((v) => v.asset_id !== id)); };
  const show = async () => setPreview((await api.post("/missions/asset-preview", { refs: value, language })).data.assets);
  return (
    <fieldset className="nb-input h-auto py-1 sm:col-span-3" data-testid="lib-assign-assets">
      <legend className="text-[11px] font-bold text-gray-500 px-1">{t("al.p.attachResources")}</legend>
      {rows.length === 0 && <p className="text-xs text-gray-500">{t("al.p.noCompatible")}</p>}
      <div className="max-h-28 overflow-y-auto">{rows.map((a) => (
        <label key={a.asset_id} className="flex items-center gap-2 text-xs py-0.5">
          <input type="checkbox" checked={value.some((v) => v.asset_id === a.asset_id)} onChange={(e) => toggle(a.asset_id, e.target.checked)} data-testid={`lib-attach-asset-${a.asset_id}`} />
          <span dir="auto">{a.title}</span> <span className="text-gray-500">· {a.cefr} · {t(`al.fam.${a.family}`)}</span>
        </label>))}</div>
      {value.length > 0 && <button type="button" onClick={show} className="text-xs underline mt-1" data-testid="lib-assets-preview">{t("al.p.previewCombined")}</button>}
      {preview && <div className="space-y-3 mt-2" data-testid="lib-assets-preview-panel">{preview.map((a) => (
        <div key={a.asset_id} className="border-t border-ink/10 pt-2"><div className="font-bold text-sm" dir="auto">{a.title}</div>
          <AssetPlayer family={a.family} language={a.language} content={a.content} /></div>))}</div>}
    </fieldset>
  );
}
